import React from 'react';
import { Users, Award, Heart, Leaf, Briefcase, TreePine, Mail, PhoneCall, CheckCircle2, Clock, Target, Sprout } from 'lucide-react';
import { cn } from '../lib/utils';

export function About() {
  const stats = [
    { 
      label: 'Entreprises partenaires',
      value: '200+',
      description: 'Qui nous font confiance'
    },
    { 
      label: 'Offres publiées',
      value: '1000+',
      description: 'Chaque année'
    },
    { 
      label: 'Candidats placés',
      value: '500+',
      description: 'Avec succès'
    },
    { 
      label: "Années d'expérience",
      value: '10+',
      description: 'Dans le secteur'
    },
  ];

  const values = [
    {
      icon: Users,
      title: 'Proximité',
      description: 'Une approche personnalisée pour chaque candidat et entreprise, basée sur l\'écoute et la compréhension.',
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'Un engagement constant vers la qualité et la performance dans tous nos services.',
    },
    {
      icon: Heart,
      title: 'Engagement',
      description: 'Une implication totale dans la réussite de nos partenaires et la satisfaction de nos candidats.',
    },
    {
      icon: Leaf,
      title: 'Durabilité',
      description: 'La promotion active de pratiques durables dans le secteur du paysagisme pour un avenir plus vert.',
    },
  ];

  const specialties = [
    { 
      icon: Briefcase,
      label: 'Paysagiste',
      description: 'Création et aménagement'
    },
    { 
      icon: TreePine,
      label: 'Jardinier',
      description: 'Entretien et maintenance'
    },
    { 
      icon: Leaf,
      label: 'Élagueur',
      description: 'Taille et soins des arbres'
    },
    { 
      icon: Sprout,
      label: 'Chef d\'équipe',
      description: 'Gestion et coordination'
    },
  ];

  const benefits = [
    { icon: Target, text: 'Recrutement ciblé et efficace' },
    { icon: Clock, text: 'Gain de temps considérable' },
    { icon: CheckCircle2, text: 'Processus simplifié' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Enhanced Parallax Effect */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/589/garden-grass-lawn-meadow.jpg')] bg-cover bg-center bg-fixed" />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/95 via-teal-800/90 to-teal-900/95" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-8 tracking-tight">
            Bienvenue sur <span className="text-teal-400">Emploi Paysagiste</span>
          </h1>
          <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto leading-relaxed">
            La plateforme de référence pour l'emploi dans les métiers du paysage et des espaces verts
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 px-6 py-3 rounded-full text-teal-50">
                <benefit.icon className="h-5 w-5" />
                <span className="text-sm font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section with Enhanced Cards */}
      <section className="py-16 -mt-24 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-8 text-center shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                <div className="text-4xl font-bold text-teal-600 mb-2">
                  {stat.value}
                </div>
                <div className="text-sm font-medium text-gray-900 mb-1">
                  {stat.label}
                </div>
                <div className="text-xs text-gray-500">
                  {stat.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section with Enhanced Design */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Notre Mission
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Offrir un espace simple, efficace et spécialisé pour trouver un emploi dans les espaces verts, 
              que ce soit en intérim, CDD, CDI ou missions saisonnières. Nous connectons les talents avec 
              les meilleures opportunités du secteur.
            </p>
          </div>
        </div>
      </section>

      {/* Specialties Section with Enhanced Cards */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos Spécialités
            </h2>
            <p className="text-xl text-gray-600">
              Des opportunités pour tous les métiers du paysage
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {specialties.map((specialty, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl p-8 text-center border border-gray-200 transform transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
              >
                <div className="w-20 h-20 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-6 transform transition-transform group-hover:rotate-6">
                  <specialty.icon className="h-10 w-10 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {specialty.label}
                </h3>
                <p className="text-sm text-gray-500">
                  {specialty.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section with Enhanced Design */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50/50 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-xl text-gray-600">
              Les principes qui guident nos actions au quotidien
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={cn(
                  "group bg-white rounded-xl p-8",
                  "border border-gray-200",
                  "transform transition-all duration-300",
                  "hover:shadow-xl hover:-translate-y-1"
                )}
              >
                <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mb-6 transform transition-transform group-hover:scale-110">
                  <value.icon className="h-8 w-8 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section with Enhanced Design */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Contactez-nous
            </h2>
            <p className="text-xl text-gray-600">
              Vous avez des questions ? Notre équipe est là pour vous aider
            </p>
          </div>
          <div className="max-w-lg mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
            <a
              href="mailto:contact@emploi-paysagiste.fr"
              className="group flex items-center justify-center gap-4 p-8 bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center transform transition-transform group-hover:rotate-6">
                <Mail className="h-6 w-6 text-teal-600" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900">Email</div>
                <div className="text-sm text-gray-500">Écrivez-nous</div>
              </div>
            </a>
            <a
              href="tel:+33745800515"
              className="group flex items-center justify-center gap-4 p-8 bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center transform transition-transform group-hover:rotate-6">
                <PhoneCall className="h-6 w-6 text-teal-600" />
              </div>
              <div className="text-left">
                <div className="font-medium text-gray-900">Téléphone</div>
                <div className="text-sm text-gray-500">Appelez-nous</div>
              </div>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}